import { Injectable } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { DfConfirmationDialogComponent, DfDialogService } from '@df/design-system-components';

@Injectable({
  providedIn: 'root'
})
export class ConfirmationModalService {

  private _dialogRef: DialogRef<boolean, DfConfirmationDialogComponent> | null = null;

  constructor(private _dfDialogService: DfDialogService) { }

  /**
   * Opens the df confirmation dialog: the first button is the confirm action, the second one the cancel action.
   * Each button method is responsible for calling closeModal().
   */
  openModal(modalParams: IConfirmationModalParams): void {
    this.closeModal();

    const [confirmButton, cancelButton] = modalParams.buttons;
    const [title, ...details] = this._toLines(modalParams.title);

    this._dialogRef = this._dfDialogService.openConfirmationDialog({
      title,
      text: details.join(' '),
      confirmLabel: confirmButton.text,
      cancelLabel: cancelButton?.text,
      variant: modalParams.hideControls ? 'warning' : 'info',
      onConfirmCallback: () => { confirmButton.method(); },
      onCancelCallback: () => { cancelButton?.method(); }
    }, { disableClose: true });
  }

  closeModal(): void {
    this._dialogRef?.close();
    this._dialogRef = null;
  }

  // Titles are built as HTML (<strong>, <br>, <div>): the df dialog displays plain text
  private _toLines(html: string): string[] {
    // DOMParser documents are inert: no image loading nor event handlers
    const text = new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, '\n'), 'text/html').body.textContent ?? '';
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => !!line);
  }
}

export interface IConfirmationModalParams {
  title: string;
  buttons: IConfirmationModalButton[];
  hideControls?: boolean;
}

export interface IConfirmationModalButton {
  text: string;
  method: () => Promise<void>;
}
